import { useQuery } from "@tanstack/react-query";
import { api } from "../api";

/** The signed-in principal, including the role the API enforces. */
export function useMe() {
  return useQuery({ queryKey: ["me"], queryFn: api.me, staleTime: 60_000 });
}

/**
 * Settings, integrations, the factory floor, runs, the merge queue, shipping releases, and
 * anything that skips review. Hides controls the API would refuse for anyone who is not an admin.
 */
export function useIsAdmin(): boolean {
  return useMe().data?.role === "admin";
}

/** Edit and route issues, sign off any issue, manage teams, place issues in releases, and stop previews. */
export function useIsManager(): boolean {
  const role = useMe().data?.role;
  return role === "admin" || role === "manager";
}

/** Create issues, and open Teams and Analytics. Everyone but viewers. */
export function useIsMember(): boolean {
  const role = useMe().data?.role;
  return role === "admin" || role === "manager" || role === "user";
}

/**
 * The Factory tab on an issue. Managers read the work order: its route, station summaries, spend
 * and artifacts. Run timelines, prompts and final messages, and the controls that act on runs or
 * the line, stay admin only.
 */
export function useFactoryAccess(): { tab: boolean; runDetail: boolean; actions: boolean } {
  const role = useMe().data?.role;
  const admin = role === "admin";
  return { tab: admin || role === "manager", runDetail: admin, actions: admin };
}

/**
 * Sections a manager may send an issue to. Later stations would skip review.
 * Keep in step with `MANAGER_START_SECTIONS` in `packages/functions/src/permissions.ts`.
 */
export const MANAGER_START_SECTIONS = new Set(["intake", "assembly"]);
